import { createServerFn } from '@tanstack/react-start'
import type { Product } from '#bv/data/products'
import { normalizeFeaturedProductIds } from '#bv/data/productFamilies'
import { readStoreFile } from '#bv/lib/adminStoreFile'
import { dbGetFeaturedProductIds } from '#bv/lib/adminStoreSupabase'
import { useSupabaseAdminStore } from '#bv/lib/supabaseAdminServer'

/** Public read for homepage featured grid (server-only). */
export const getFeaturedPanelProductIds = createServerFn({ method: 'GET' }).handler(async (): Promise<number[]> => {
  const raw = useSupabaseAdminStore()
    ? await dbGetFeaturedProductIds()
    : (readStoreFile().featuredProductIds ?? [])
  return normalizeFeaturedProductIds(raw)
})

/** Curated IDs first (in order), then backfill to `max` so new SKUs still surface on the homepage. */
export function resolveFeaturedDeals(shopGrid: Product[], configuredIds: number[], max = 4): Product[] {
  const map = new Map(shopGrid.map((p) => [p.id, p]))
  const picked: Product[] = []
  const seen = new Set<number>()

  for (const id of normalizeFeaturedProductIds(configuredIds)) {
    const p = map.get(id)
    if (p && !seen.has(p.id)) {
      picked.push(p)
      seen.add(p.id)
      if (picked.length >= max) return picked
    }
  }

  const preferred = shopGrid.filter((p) => (p.badge === 'bestseller' || p.badge === 'popular') && !seen.has(p.id))
  for (const p of preferred) {
    picked.push(p)
    seen.add(p.id)
    if (picked.length >= max) return picked
  }

  for (const p of shopGrid) {
    if (seen.has(p.id)) continue
    picked.push(p)
    seen.add(p.id)
    if (picked.length >= max) return picked
  }

  return picked
}
